import React, { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Star, Loader2, Send } from "lucide-react";

const StarRating = ({ value, onChange, label }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-1">
      <Label className="text-sm text-gray-600">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-0.5 transition-transform hover:scale-110"
          >
            <Star
              className={`w-6 h-6 transition-colors ${
                star <= (hover || value)
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-300"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default function ReviewForm({ jobId, professionalId, professionalName, onReviewSubmitted }) {
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");
  
  const [ratings, setRatings] = useState({
    overall: 0,
    quality: 0,
    communication: 0,
    value: 0
  });
  const [reviewText, setReviewText] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (ratings.overall === 0 || ratings.quality === 0 || 
        ratings.communication === 0 || ratings.value === 0) {
      toast.error("Please rate all categories");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/reviews`,
        {
          professional_id: professionalId,
          job_id: jobId,
          overall_rating: ratings.overall,
          quality_rating: ratings.quality,
          communication_rating: ratings.communication,
          value_rating: ratings.value,
          review_text: reviewText.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Thank you for your review!");
      if (onReviewSubmitted) onReviewSubmitted();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6 bg-white rounded-xl border border-gray-200">
      <div>
        <h3 className="font-heading font-semibold text-lg text-gray-900 mb-1">
          Review {professionalName}
        </h3>
        <p className="text-sm text-gray-500">
          Share your experience to help other customers
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StarRating
          value={ratings.overall}
          onChange={(v) => setRatings({...ratings, overall: v})}
          label="Overall Rating"
        />
        <StarRating
          value={ratings.quality}
          onChange={(v) => setRatings({...ratings, quality: v})}
          label="Quality of Work"
        />
        <StarRating
          value={ratings.communication}
          onChange={(v) => setRatings({...ratings, communication: v})}
          label="Communication"
        />
        <StarRating
          value={ratings.value}
          onChange={(v) => setRatings({...ratings, value: v})}
          label="Value for Money"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="review_text">Written Review (Optional)</Label>
        <Textarea
          id="review_text"
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          placeholder="Tell others about your experience..."
          className="min-h-[100px]"
          data-testid="review-text-input"
        />
      </div>

      <Button
        type="submit"
        className="w-full bg-forest hover:bg-forest-dark rounded-full"
        disabled={loading}
        data-testid="submit-review-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Submit Review
          </>
        )}
      </Button>
    </form>
  );
}

// Display component for showing reviews
export function ReviewsList({ reviews }) {
  if (!reviews || reviews.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Star className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p>No reviews yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => (
        <div key={review.id} className="p-4 bg-gray-50 rounded-lg" data-testid={`review-${review.id}`}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-medium text-gray-900">{review.customer_name}</p>
              <p className="text-xs text-gray-500">
                {new Date(review.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="font-semibold">{review.overall_rating}</span>
            </div>
          </div>
          
          <div className="flex gap-4 text-xs text-gray-500 mb-3">
            <span>Quality: {review.quality_rating}/5</span>
            <span>Communication: {review.communication_rating}/5</span>
            <span>Value: {review.value_rating}/5</span>
          </div>
          
          {review.review_text && (
            <p className="text-gray-700 text-sm">{review.review_text}</p>
          )}
        </div>
      ))}
    </div>
  );
}
